import { createUseStyles } from 'react-jss';
import { Theme } from '@mui/material';

export const useStyles = createUseStyles((theme: Theme) => {
    const { color, layout, radius, mq, motion } = theme.custom;
    return {
        root: {
            width: layout.navWidth,
            flexShrink: 0,
            backgroundColor: color.surface,
            borderRight: `1px solid ${color.border}`,
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingBottom: 24,
            transitionProperty: 'transform, box-shadow',
            transitionDuration: motion.slow,
            transitionTimingFunction: motion.ease,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { borderRadius: 3, backgroundColor: color.border },
            [mq.sm]: {
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: 0,
                height: '100vh',
                width: layout.navWidthMobile,
                zIndex: 1200,
                transform: 'translateX(-100%)',
                backgroundColor: color.surfaceRaised,
                boxShadow: 'none',
            },
        },
        mobileOpen: {
            [mq.sm]: { transform: 'translateX(0)', boxShadow: '8px 0 32px rgba(0, 0, 0, 0.24)' },
        },
        mobileHeader: {
            display: 'none',
            [mq.sm]: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 8px 8px 16px',
                borderBottom: `1px solid ${color.border}`,
                minHeight: layout.headerHeight,
            },
        },
        mobileTitle: { fontSize: 14, fontWeight: 600, color: color.text.primary },
        mobileCloseBtn: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            cursor: 'pointer',
            borderRadius: radius.sm,
            border: 'none',
            backgroundColor: 'transparent',
            color: color.text.secondary,
            '&:hover': { backgroundColor: theme.palette.action.hover, color: color.text.primary },
        },
        navContent: { padding: '8px 0' },
        itemsWrapper: { display: 'flex', flexDirection: 'column', gap: 2, padding: '2px 10px 10px' },
        itemIcon: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            marginRight: 10,
            color: color.text.secondary,
            flexShrink: 0,
            transitionProperty: 'color',
            transitionDuration: motion.fast,
            '& svg': { fontSize: 20 },
        },
        itemTitle: {
            fontSize: 14,
            fontWeight: 500,
            color: color.text.primary,
            lineHeight: 1.3,
            transitionProperty: 'color',
            transitionDuration: motion.fast,
        },
        itemContainer: {
            display: 'flex',
            width: '100%',
            cursor: 'pointer',
            textDecoration: 'none',
            color: color.text.primary,
            borderRadius: radius.md,
            boxSizing: 'border-box',
            transitionProperty: 'background-color, color',
            transitionDuration: motion.fast,
            '&:hover': {
                textDecoration: 'none',
                backgroundColor: theme.palette.action.hover,
                '& $itemIcon': { color: color.text.primary },
            },
            '&:focus-visible': { outline: `2px solid ${color.focusRing}`, outlineOffset: -2 },
        },
        linkContent: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minHeight: 40,
            padding: '8px 10px',
            boxSizing: 'border-box',
        },
        activeRoute: {
            cursor: 'default',
            backgroundColor: color.primary.soft,
            '& $itemIcon': { color: color.primary.main },
            '& $itemTitle': { fontWeight: 600, color: color.primary.onSoft },
            '&:hover': {
                backgroundColor: color.primary.soft,
                '& $itemIcon': { color: color.primary.main },
            },
        },
        sectionHeader: {
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: color.text.secondary,
        },
        sectionHeaderArchived: {
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: color.text.secondary,
        },
        customAcordion: {
            backgroundColor: 'transparent',
            color: color.text.primary,
            boxShadow: 'none',
            border: 'none',
            borderRadius: '0 !important',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
            '& .MuiAccordionSummary-root': {
                minHeight: 40,
                padding: '0 14px 0 20px',
                margin: 0,
                backgroundColor: 'transparent',
                borderRadius: 0,
                '&:hover': { backgroundColor: theme.palette.action.hover },
                '&.Mui-expanded': {
                    minHeight: 40,
                    '& $sectionHeader': { color: color.text.primary },
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                    color: color.text.muted,
                    '& svg': { fontSize: 20 },
                    '&.Mui-expanded': { transform: 'rotate(180deg)' },
                },
            },
            '& .MuiAccordionSummary-content': {
                margin: '10px 0',
                '&.Mui-expanded': { margin: '10px 0' },
            },
            '& .MuiAccordionDetails-root': { padding: 0, backgroundColor: 'transparent' },
        },
        nestedAccordion: {
            margin: '0 10px 10px',
            borderRadius: `${radius.md}px !important`,
            overflow: 'hidden',
            backgroundColor: color.surfaceMuted,
            '& .MuiAccordionSummary-root': { minHeight: 36, padding: '0 10px 0 12px' },
            '& .MuiAccordionSummary-content': { margin: '8px 0', '&.Mui-expanded': { margin: '8px 0' } },
            '& .MuiAccordionDetails-root': { padding: '0 0 2px 6px' },
        },
    };
});
